// The Product Detail page's field-catalog rendering. Follows
// utils/searchResultsFieldFormat.js's pattern (i18n label lookup with a $te()-guarded fallback,
// formatter dispatch by field.type) applied to DetailPageResponse's `{ key, value, type }` field
// bags (Sidebar/Payout sections) instead of search's flat column list — see
// DETAIL_PAGE_DESIGN_FE.md for why this isn't a literal reuse of that file: Booleans here
// render as the existing segmented Presente/Assente control (see AdditionalFeatures.vue,
// `common.present`/`common.absent`), not the search grid's plain Sì/No text, and labels are
// namespaced by sectionKey (`productDetails.fields.<sectionKey>.<fieldKey>`) since this catalog
// can reuse the same field_key across sections with a different meaning.
//
// `field.key` (from DetailPageEndpoint) is already lowerFirst-cased by the backend's `ToWireKey` —
// every i18n key this file builds uses that same casing, so no case conversion is needed here.

import { rhpToYears } from './formatter'

function upperFirst(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function detailFieldLabel(ctx, sectionKey, fieldKey) {
  const key = `productDetails.fields.${sectionKey}.${fieldKey}`
  return ctx.$te(key) ? ctx.$t(key) : fieldKey
}

export function detailSectionTitle(ctx, sectionKey) {
  const key = `productDetails.sectionTitle.${sectionKey}`
  return ctx.$te(key) ? ctx.$t(key) : sectionKey
}

export function detailTabLabel(ctx, tabKey) {
  const key = `productDetails.tabHeader.${tabKey}`
  return ctx.$te(key) ? ctx.$t(key) : tabKey
}

// Enum values project as the .NET enum member name (e.g. "TrackerCertificate"), not a numeric code
// — see DetailFieldProjectorRegistry on the BE — so they resolve against the SAME app-wide
// `search.options.<EnumName>.<memberName>` maps already authored (and user-confirmed) for the
// filter catalog's MultiSelect options, rather than a second `productDetails.options.*` copy of
// the same labels. Two precedents, both deliberate, both for the same reason: the search-results
// grid (utils/searchResultsFieldFormat.js's resultFieldEnumLabel) and this page's own
// typedSections/UnderlyingsSection.vue (UnderlyingAssetClass's 8 labels). The enum name is
// upperFirst(field_key) for every Enum-typed row in the detail catalog — field_key follows the
// `Product` property name, which is the enum's own name for all of them (AcepiCategory,
// ProtectionType, CouponType, CouponFrequency, CouponConditioning) — so no alias map is needed
// here, unlike searchResultsFieldFormat's three legacy exceptions.
//
// `CouponFrequency` is the one enum whose option labels exist only for this page (it is neither a
// filter nor a search-results column today); it still lives under `search.options.*` so all
// enum-member labels stay in one place.
//
// KiidDetail's kiidFundCategory/kiidUnderlyingType are the exception, not a gap: they carry the
// SAME OpenKid.Model FundCategory/UnderlyingType enums the
// `filterSelectValues.optionFundCategory`/`optionUnderlyingType` dictionaries already label (the
// app-wide mixin, main.js — reused verbatim by UnderlyingOptions.vue/InvestmentOptionsProfileSection.vue
// elsewhere on this page) — those dictionaries are keyed by the enum's NUMERIC code, not the member
// name this file receives, so *_CODE_BY_NAME below bridges the two, same "reuse via a name→code map"
// pattern already used for PremiumType/ProductType (AdditionalInfoSection.vue) and KidType
// (InvestmentOptionsProfileSection.vue) — verified against OpenKid.Model/FundCategory.cs and
// UnderlyingType.cs directly, not guessed.
const FUND_CATEGORY_CODE_BY_NAME = { NotAvailable: 0, Equity: 1, Bond: 2, Balanced: 3, Flexible: 4, MoneyMarket: 5, Other: 6 }
const UNDERLYING_TYPE_CODE_BY_NAME = { NotAvailable: 0, ExternalFund: 1, InternalFund: 2, FundsPortfolio: 3, SegregatedFund: 4, Combination: 5 }
// Sidebar/Characteristics (Ibips) — same "reuse via a name→code map" pattern, bridging to the old
// page's own filterSelectValues.* dictionaries (search.filterSelectValues.* in the locale files,
// verified index-for-index against OpenKid.Model's StructureType/ProductType/PremiumType/KidType/
// MopDocumentStructure/DistributionChannel enums directly, not guessed) instead of seeding a parallel
// productDetails.options.* translation for each one.
const STRUCTURE_TYPE_CODE_BY_NAME = { Sop: 1, Mop: 2 }
const PRODUCT_TYPE_CODE_BY_NAME = { NotAvailable: 0, RamoI: 1, RamoIII: 2, MultiRamo: 3, RamoV: 4 }
const PREMIUM_TYPE_CODE_BY_NAME = { NotAvailable: 0, Single: 1, Regular: 2, Mixed: 3 }
const KID_TYPE_CODE_BY_NAME = { Standard: 0, Pure: 1, Hybrid: 2 }
const MOP_DOCUMENT_STRUCTURE_CODE_BY_NAME = { Mop10a: 1, Mop10b: 2 }
const DISTRIBUTION_CHANNEL_CODE_BY_NAME = { NotAvailable: 0, Agent: 1, Bank: 2, Broker: 3, Advisor: 4 }
const LEGACY_ENUM_LOOKUP_BY_FIELD_KEY = {
  kiidFundCategory: { codeByName: FUND_CATEGORY_CODE_BY_NAME, dictionaryKey: 'optionFundCategory' },
  kiidUnderlyingType: { codeByName: UNDERLYING_TYPE_CODE_BY_NAME, dictionaryKey: 'optionUnderlyingType' },
  structureType: { codeByName: STRUCTURE_TYPE_CODE_BY_NAME, dictionaryKey: 'productStructures' },
  productType: { codeByName: PRODUCT_TYPE_CODE_BY_NAME, dictionaryKey: 'productTypes' },
  premiumType: { codeByName: PREMIUM_TYPE_CODE_BY_NAME, dictionaryKey: 'premia' },
  kidType: { codeByName: KID_TYPE_CODE_BY_NAME, dictionaryKey: 'kidTypes' },
  mopDocumentStructure: { codeByName: MOP_DOCUMENT_STRUCTURE_CODE_BY_NAME, dictionaryKey: 'mds' },
  distributionChannels: { codeByName: DISTRIBUTION_CHANNEL_CODE_BY_NAME, dictionaryKey: 'distributionChannels' },
}

function detailFieldEnumLabel(ctx, fieldKey, value) {
  if (value === null || value === undefined) {
    return ctx.$t('common.notAvailable')
  }
  const legacyLookup = LEGACY_ENUM_LOOKUP_BY_FIELD_KEY[fieldKey]
  if (legacyLookup) {
    const code = legacyLookup.codeByName[value]
    const label = code != null ? ctx.filterSelectValues[legacyLookup.dictionaryKey][code] : null
    if (label != null) {
      return label
    }
  }
  const key = `search.options.${upperFirst(fieldKey)}.${value}`
  return ctx.$te(key) ? ctx.$t(key) : value
}

// "Termina dopo N anni/mesi" caption for an autocallable/callable IC product's scenario row
// (backend DETAIL_PAGE_DESIGN.md/ScenarioRow.Period1CaptionPeriod — one caption per scenario row,
// rendered in ProductScenarios.vue's label column under the scenario name, not a shared column
// header — see that component's own period1Caption()). `years` is IC_SCENARIO_PERIODS_DESIGN.md's
// fraction-of-year double (DOXA's own contract, e.g. 1.5 = 18 months, 0.75 = 9 months) — rounded to
// the nearest whole month, then: whole years only when the remainder is exactly 0 (e.g. 60 months →
// "5 anni"), months only under 12 (e.g. 9 months → "9 mesi"), both combined above 12 when not an
// exact multiple (e.g. 42 months → "3 anni e 6 mesi") — user-confirmed rule, all three shapes
// explicitly requested. Returns null when there is no caption for this row (the common case — only
// autocallable/callable products populate Period1CaptionPeriod at all).
export function detailScenarioTerminationCaption(ctx, years) {
  if (years == null) {
    return null
  }
  const totalMonths = Math.round(years * 12)
  const wholeYears = Math.floor(totalMonths / 12)
  const remainingMonths = totalMonths % 12

  let duration
  if (remainingMonths === 0) {
    duration = ctx.$tc('productDetails.terminationCaption.years', wholeYears, { n: wholeYears })
  } else if (wholeYears === 0) {
    duration = ctx.$tc('productDetails.terminationCaption.months', remainingMonths, { n: remainingMonths })
  } else {
    duration = ctx.$t('productDetails.terminationCaption.yearsAndMonths', {
      years: ctx.$tc('productDetails.terminationCaption.years', wholeYears, { n: wholeYears }),
      months: ctx.$tc('productDetails.terminationCaption.months', remainingMonths, { n: remainingMonths }),
    })
  }
  return ctx.$t('productDetails.terminationCaption.prefix', { duration })
}

// Sidebar/Characteristics (Ibips) — "rhpMin"/"rhpMax" are raw fractional-year doubles (field_type
// Number) that need the "5y"/"1y 6m" formatting (ProductCharacteristics.vue's
// `rhpToYears(product.rhpMin)`), not a plain number — reused verbatim rather than re-derived here,
// same "field-key bypasses the generic type dispatch" precedent as LEGACY_ENUM_LOOKUP_BY_FIELD_KEY
// above.
const RHP_FIELD_KEYS = new Set(['rhpMin', 'rhpMax'])

// When a section carries both rhpMin and rhpMax (MOP products with an RHP range), they're shown as
// a single "RHP" row ("1y - 8y") placed where rhpMin was; the merged field's value is `{ min, max }`.
// Equal (or one-sided) bounds collapse to one value, so SOP products still read "5y".
export function mergeRhpFields(fields) {
  const min = fields.find((f) => f.key === 'rhpMin')
  const max = fields.find((f) => f.key === 'rhpMax')
  if (!min || !max) {
    return fields
  }
  const merged = { key: 'rhp', type: min.type, value: { min: min.value, max: max.value } }
  return fields.filter((f) => f !== max).map((f) => (f === min ? merged : f))
}

function rhpRangeText(ctx, { min, max }) {
  if (min == null && max == null) {
    return ctx.$t('common.notAvailable')
  }
  if (min == null || max == null || min === max) {
    return rhpToYears(min != null ? min : max)
  }
  return `${rhpToYears(min)} - ${rhpToYears(max)}`
}

// Returns `{ isBoolean: true, boolValue }` for Boolean fields — FieldCatalogSection.vue renders the
// segmented control itself for those — or `{ text }` for every other type, already formatted and
// "N/D"-defaulted (backend DETAIL_PAGE_DESIGN.md's null-handling convention).
export function detailFieldDisplayValue(ctx, field) {
  const { key, value, type } = field
  if (key === 'rhp' && value && typeof value === 'object') {
    return { text: rhpRangeText(ctx, value) }
  }
  if (RHP_FIELD_KEYS.has(key)) {
    return { text: value != null ? rhpToYears(value) : ctx.$t('common.notAvailable') }
  }
  switch (type) {
    case 'Boolean':
      return { isBoolean: true, boolValue: value }
    case 'Percent':
      return { text: value != null ? ctx.$n(value, 'percent') : ctx.$t('common.notAvailable') }
    case 'Date':
      return { text: value ? ctx.$d(new Date(value), 'short') : ctx.$t('common.notAvailable') }
    case 'Enum':
      return { text: detailFieldEnumLabel(ctx, key, value) }
    case 'EnumList':
      return {
        text:
          Array.isArray(value) && value.length > 0
            ? value.map((v) => detailFieldEnumLabel(ctx, key, v)).join(', ')
            : ctx.$t('common.notAvailable'),
      }
    default:
      return { text: value != null && value !== '' ? value : ctx.$t('common.notAvailable') }
  }
}
